import React, { useState, useEffect, useCallback } from "react";
import { Grid, CircularProgress, Card, CardContent, CardHeader } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { HazardsAssessmentWithTabs } from "../riskAssessment/HazardsAssessment";
import { PolicyShowMap } from "./PolicyShowMap";
import { getHazardsByUserAndPage } from "../riskAssessment/hazardUtils";
import { PolicyDetails } from "../riskAssessment/hazardIdEnum";
import { HazardProvider } from "../riskAssessment/HazardMapContext";
import httpClient from "../../lib/httpClient";
import { getLatestUpdateMonth, getLatestUpdateDaily } from "../util/tableUtils";
import { saveUserMonitorData } from "../util/userUtils";
import { PolicyDescriptionWithLocation } from "./PolicyDescriptionWithLocation";

import { CoverageList } from "./CoverageList";
import { RamoTransitorioList } from "./RamoTransitorioList";
import { PolicyClaimsList } from "./PolicyClaimsList";
import { emptyGeojson } from "../riskAssessment/defaultSourceLayer";

import RiskIndexEvaluation from "./RiskIndexEvaluation";

import { apiError } from "../../lib/apiError";

// TODO: find a type for props, policy and claim
export const PolicyShow = (props: any) => {
  const policyId = props.match.params.id;
  const multilocationId = props.match.params.mlid;
  const locationStartDateString = props.match.params.locationStartDateString;
  const { t } = useTranslation();

  const [hazardsList, setHazardsList] = useState([] as Hazard[]);
  const [policy, setPolicy] = useState<any>(null);
  const [longitude, setLongitude] = useState<number>(0);
  const [latitude, setLatitude] = useState<number>(0);

  const [isPolicyFetching, setIsPolicyFetching] = useState(false);
  const [lastUpdateDate, setLastUpdateDate] = useState("");
  const [buildingsLastUpdateDate, setBuildingsLastUpdateDate] = useState("");

  const [buildingGeoJsonValue, setBuildingGeoJsonValue] = useState<GeoJsonData>(emptyGeojson);
  const [roofarea, setRoofarea] = useState("...");
  const [buildingHeight, setBuildingHeight] = useState("...");
  const [groundLevel, setGroundLevel] = useState("...");

  const getRoofDetailsData = useCallback(async () => {
    if (!latitude || !longitude) return;

    setBuildingHeight(".....");
    setRoofarea(".....");
    setGroundLevel(".....");
    setBuildingGeoJsonValue(emptyGeojson);

    // Example: lat=45.04159&lon=7.67453
    const resultFromRoofGeometry = await httpClient().get(
      `/proxy/roofGeometry?lat=${latitude}&lon=${longitude}`,
    );

    if (resultFromRoofGeometry?.data?.roof_geojsons?.length > 0) {
      setBuildingGeoJsonValue(resultFromRoofGeometry.data.roof_geojsons[0]);
    } else {
      setBuildingGeoJsonValue(emptyGeojson);
    }

    if (resultFromRoofGeometry?.data?.area) {
      setRoofarea(resultFromRoofGeometry.data.area);
    } else {
      setRoofarea("N/A");
    }

    if (resultFromRoofGeometry?.data?.building_height) {
      setBuildingHeight(resultFromRoofGeometry.data.building_height);
    } else {
      setBuildingHeight("N/A");
    }

    if (resultFromRoofGeometry?.data?.ground_level || resultFromRoofGeometry?.data?.ground_level === 0) {
      const asInt = parseInt(resultFromRoofGeometry.data.ground_level);
      setGroundLevel(asInt + "..." + (asInt + 10));
    } else {
      setGroundLevel("N/A");
    }
  }, [latitude, longitude]);

  useEffect(() => {
    setIsPolicyFetching(true);

    const getPolicyDetailsData = async () => {
      saveUserMonitorData("policyShow", policyId);
      try {
        const result = await httpClient().get(
          `/climatedb/${policyId}/${multilocationId}/${locationStartDateString}`,
        );
        const { data: policyResult } = result;
        setPolicy(policyResult);

        const [longitude, latitude] = policyResult.location.coordinates;
        setLongitude(longitude);
        setLatitude(latitude);
      } catch (error) {
        apiError(error);
      }
    };

    const currentHazards = getHazardsByUserAndPage(PolicyDetails);
    setHazardsList(currentHazards);

    getLatestUpdateMonth("/serviceLogs/buildings?service=db_climate").then((lastUpdateMsg) => {
      setLastUpdateDate(lastUpdateMsg);
    });

    getLatestUpdateDaily("/serviceLogs/buildings?service=db_claims_daily").then((lastUpdateMsg) => {
      setBuildingsLastUpdateDate(lastUpdateMsg);
    });

    getPolicyDetailsData();
    setIsPolicyFetching(false);
  }, [policyId]);

  useEffect(() => {
    getRoofDetailsData();
  }, [getRoofDetailsData]);

  return policy ? (
    <HazardProvider>
      <Grid container spacing={1} style={{ marginTop: 15 }}>
        <Grid item xs={8} sm={10} md={4}>
          <Card>
            <CardHeader title={t("policy.description")} />

            {isPolicyFetching ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <CardContent style={{ paddingTop: "0" }}>
                <PolicyDescriptionWithLocation
                  policy={policy}
                  roofarea={roofarea}
                  buildingHeight={buildingHeight}
                  groundLevel={groundLevel}
                />
              </CardContent>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          {latitude && longitude ? (
            <PolicyShowMap
              latitude={latitude}
              longitude={longitude}
              buildingGeoJsonProp={buildingGeoJsonValue}
            ></PolicyShowMap>
          ) : (
            <Grid container justifyContent="center">
              <CircularProgress />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} sm={12}>
        {latitude && longitude ? (
          <HazardsAssessmentWithTabs
            hazardsList={hazardsList}
            latitude={latitude}
            longitude={longitude}
          ></HazardsAssessmentWithTabs>
        ) : (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        )}
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <CoverageList policyId={policyId} lastUpdate={lastUpdateDate} />
        </Grid>
        <Grid item xs={12}>
          <RamoTransitorioList
            perils={policy?.perils || []}
            isLoading={isPolicyFetching}
            lastUpdate={buildingsLastUpdateDate}
          />
        </Grid>
        <Grid item xs={12}>
          <PolicyClaimsList
            claims={policy?.climatePolicies?.filter((claim: any) => claim.claimCode) || []}
            isLoading={isPolicyFetching}
            lastUpdate={lastUpdateDate}
          />
        </Grid>

        <Grid item xs={12}>
          <RiskIndexEvaluation istat={policy?.addressCityCode} />
        </Grid>
      </Grid>
    </HazardProvider>
  ) : (
    <></>
  );
};
